import { useDebounce } from '@uidotdev/usehooks';
import { DraftShareBody } from 'components/drafts/DraftShareBody';
import { ShareBody, ShareDialog } from 'components/share/ShareDialog';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import useAuth from 'hooks/useAuth';
import { useState } from 'react';
import { useGetDraftQuery, useGetDraftShareTargetsQuery, useShareDraftMutation } from 'store/api/drafts';
import type { DraftDetail, DraftKey, DraftShare } from 'types/drafts';
import { successAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { type NamedEntityParts, useEmployeeNameFormatter } from 'utils/nameOrder';

type Props = {
    draft: DraftKey;
    open: boolean;
    onClose: () => void;
};

/**
 * Whom a draft is shared with, and how far: the share dialog over the draft as the server has it. Only an owner of
 * the draft opens it. Share saves every share at once and closes it.
 */
export function ShareDraftDialog({ draft: key, open, onClose }: Props) {
    const { data: draft } = useGetDraftQuery(key, { skip: !open });
    const isNewDesign = useIsNewDesign();
    return (
        <ShareDialog
            open={open}
            onClose={onClose}
            title={t('draft.share.title', { record: t(`create-dialog.record.${key.type}`) })}
            description={draft?.name ?? ''}
            ruled={false}
            // Pinned near the top, so the dialog stays put as its list grows. The backdrop is the design's own: the
            // new design's blurred, classic's darker and unblurred (the dialog is in the new design's root in both).
            className="top-15 max-h-[calc(100dvh-120px)] translate-y-0 sm:max-w-[600px]"
            overlayClassName={isNewDesign ? 'new:backdrop-blur-[3px]' : 'new:bg-black/45'}
        >
            {draft ? <ShareDraftContent draft={draft} onClose={onClose} /> : <ShareBody aria-busy className="h-60" />}
        </ShareDialog>
    );
}

/** Mounted once the draft is in, as the body keeps its own copy of the shares from then on. */
function ShareDraftContent({ draft, onClose }: { draft: DraftDetail; onClose: () => void }) {
    const { user } = useAuth();
    const [search, setSearch] = useState('');
    // Looked up once the typing pauses; the targets found last stay listed meanwhile.
    const settled = useDebounce(search, 300);
    const { data: targets = [] } = useGetDraftShareTargetsQuery(settled);
    const [shareDraft, { isLoading }] = useShareDraftMutation();
    const formatName = useEmployeeNameFormatter();

    const save = async (shares: DraftShare[]) => {
        try {
            await shareDraft({
                type: draft.type,
                id: draft.id,
                shares: shares.map(({ type, id, right }) => ({ type, id, right })),
            }).unwrap();
            const added = shares.filter(
                (share) => !draft.shares.some((each) => each.type === share.type && each.id === share.id)
            );
            successAlert(savedMessage(added, formatName));
            onClose();
        } catch {
            // The failure is reported where every request's is; the dialog stays open on what was chosen.
        }
    };

    return (
        <DraftShareBody
            // A draft whose creator is deleted still has its row, unnamed.
            creator={{
                id: draft.owner?.id ?? 0,
                name: draft.owner?.name ?? '',
                firstName: draft.owner?.firstName,
                lastName: draft.owner?.lastName,
                isYou: draft.owner?.id === user?.id,
            }}
            shares={draft.shares}
            targets={targets}
            onSearchChange={setSearch}
            records={t(`draft.records.${draft.type}`)}
            saving={isLoading}
            onSubmit={save}
            onClose={onClose}
        />
    );
}

/** The alert once the shares are saved: whom the draft is newly shared with, or, with nobody added, that access changed. */
function savedMessage(added: DraftShare[], formatName: (share: NamedEntityParts) => string): string {
    if (added.length === 0) return t('draft.toast.access-updated');
    const [first] = added;
    return added.length === 1
        ? t('draft.toast.shared', { name: formatName(first) })
        : t('draft.toast.shared-more', { name: formatName(first), count: String(added.length - 1) });
}
